import {
	BigNum,
	CandleResolution,
	DepositExplanation,
	MarketType,
	QUOTE_PRECISION_EXP,
	UserAccount,
	decodeUser,
} from '@velocity-exchange/sdk';
import { Buffer } from 'buffer';
import {
	DownloadRecordType,
	DownloadFile,
	ENUM_UTILS,
	MarketDetails24H,
	MarketId,
	Serializer,
	UISerializableCandle,
	UISerializableDepositRecord,
	UISerializableFundingPaymentRecord,
	UISerializableFundingRateRecord,
	UISerializableLiquidationRecord,
	UISerializableOrderActionRecord,
} from '@velocity-exchange/common';

const DEFAULT_PAGE_SIZE = 20;

type VolumeHistory = { date: string; volume: number };
import { JsonObject } from 'cerializr';
import { PublicKey } from '@solana/web3.js';
import Env from 'src/environmentVariables/EnvironmentVariables';
import captureException from './captureException';
import LoggingService from './LoggingService';
import { getPaginationQueryString } from 'src/utils/pagination';
import { encodeQueryParams } from 'src/utils/explorer';

const joinKeys = (pubKeys: PublicKey[]) =>
	pubKeys.map((pubKey) => pubKey.toString()).join(',');

enum RESOURCE {
	HISTORY_SERVER,
	DLOB_SERVER,
}

function throwUnhandled(eventType: never) {
	throw new Error('Unknown resource: ' + eventType);
}

type ClientResponse<T = void> = Promise<{
	success: boolean;
	body?: T;
	message?: string;
	status?: number;
}>;

const API_SECRET = process.env.NEXT_PUBLIC_INTERNAL_SECRET ?? '';

class ExchangeHistoryClient {
	private static get(url: string, resource = RESOURCE.HISTORY_SERVER) {
		const serverUrl = (() => {
			switch (resource) {
				case RESOURCE.DLOB_SERVER:
					return Env.dlobServerHttpUrl;
				case RESOURCE.HISTORY_SERVER:
					return Env.historyServerUrl;
				default:
					throwUnhandled(resource);
			}
		})();

		return new Promise<{ success: boolean; body: any; status: number }>(
			(res) => {
				const headers = new Headers();

				if (API_SECRET) {
					headers.append('Authorization', API_SECRET);
				}

				fetch(`${serverUrl}${url}`, {
					headers,
				})
					.then(async (response) => {
						if (!response.ok) {
							res({
								success: false,
								body: response.body,
								status: response.status,
							});
							return;
						}
						res({
							success: true,
							body: await response.json(),
							status: response.status,
						});
					})
					.catch((err) => {
						res({ success: false, body: err, status: 0 });
					});
			}
		);
	}

	private static post(url: string, bodyObject: any) {
		const requestOptions = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' } as Record<string, string>,
			body: JSON.stringify(bodyObject),
		};

		return new Promise<{ success: boolean; body: any; status: number }>(
			(res) => {
				if (API_SECRET) {
					requestOptions.headers['Authorization'] = API_SECRET;
				}

				const postRequest = new Request(
					`${Env.historyServerUrl}${url}`,
					requestOptions
				);

				fetch(postRequest)
					.then(async (response) => {
						if (!response.ok) {
							res({
								success: false,
								body: response.body,
								status: response.status,
							});
							return;
						}
						res({
							success: true,
							body: await response.json(),
							status: response.status,
						});
					})
					.catch((err) => {
						res({ success: false, body: err, status: 0 });
					});
			}
		);
	}

	public static async getTradesForUsers(
		userPubKeys: PublicKey[],
		pageIndex: number,
		oldPageIndex?: number,
		beforeTs?: number,
		afterTs?: number,
		marketIndex?: number,
		marketType?: MarketType,
		pageSize = DEFAULT_PAGE_SIZE,
		predictionMarketsOnly = false
	): ClientResponse<{
		data: {
			records: UISerializableOrderActionRecord[][];
			totalCounts: number[];
		};
	}> {
		try {
			const baseQuery = `/${
				predictionMarketsOnly ? 'predictionTrades' : 'trades'
			}/userAccounts/?userPublicKeys=${joinKeys(userPubKeys)}&marketIndex=${
				marketIndex ?? ''
			}&marketType=${ENUM_UTILS.toStr(marketType as MarketType) ?? ''}`;

			const queryString = getPaginationQueryString({
				baseQuery,
				newPage: pageIndex,
				pageSize,
				oldPage: oldPageIndex,
				beforeTs,
				afterTs,
			});

			const result = await this.get(queryString);

			if (!result.success) {
				return { success: false, message: `Couldn't get trades for user` };
			}

			const resultData = result.body.data as {
				records: any[][];
				totalCounts: number[];
			};

			const formattedTrades = resultData.records.map((records) =>
				records.map((record) =>
					Serializer.Deserialize.UIOrderActionRecord(record)
				)
			);

			return {
				success: true,
				body: {
					data: {
						records: formattedTrades,
						totalCounts: resultData.totalCounts,
					},
				},
			};
		} catch (e: any) {
			captureException(e);
			return {
				success: false,
				message: `Caught Error getting trades for users`,
			};
		}
	}

	public static async getCumFundingForUser(
		userPubKey: PublicKey,
		marketIndex?: number
	): ClientResponse<{ data: number }> {
		try {
			const url = `/cumFunding/userAccount?userPublicKey=${userPubKey.toString()}${
				!Number.isNaN(marketIndex) ? `&marketIndex=${marketIndex}` : ''
			}`;
			return await this.get(url);
		} catch (e: any) {
			captureException(e);
			console.log(`Caught Error getting cumulative funding for user: ${e}`);
			return {
				success: false,
				message: `Caught Error getting cumulative funding for user`,
			};
		}
	}

	/**
	 *
	 * @param resolution
	 * @param marketIndex
	 * @param from - unix timestamp ms since epoch
	 * @param to - unix timestamp ms since epoch
	 * @returns
	 */
	public static async getMarketHistory(
		resolution: CandleResolution,
		marketIndex: number,
		marketType: MarketType,
		to: number,
		from?: number,
		countBack?: number
	): ClientResponse<{
		candles: UISerializableCandle[];
		lastTradeSeen: UISerializableOrderActionRecord | undefined;
		beyondMaxLookback?: boolean;
	}> {
		try {
			const result = await (this.get(
				`/tv/history?marketIndex=${marketIndex}&marketType=${ENUM_UTILS.toStr(
					marketType
				)}&resolution=${resolution}&from=${from}&to=${to}&countBack=${countBack}`
			) as ClientResponse<{
				candles: Record<string, unknown>[];
				lastTradeSeen?: Record<string, unknown>;
				beyondMaxLookback?: boolean;
			}>);

			if (!result.success) {
				return {
					success: false,
					message: `Couldn't get market trade history`,
					status: result.status,
				};
			}

			const deserializedCandles = result.body?.candles.map((candle) => {
				// @ts-ignore
				return Serializer.Deserialize.UICandle(candle as unknown as JsonObject);
			});

			return {
				success: true,
				body: {
					candles: deserializedCandles ?? [],
					lastTradeSeen: result.body?.lastTradeSeen
						? Serializer.Deserialize.UIOrderActionRecord({
								// @ts-ignore
								...(result.body.lastTradeSeen as unknown as JsonObject),
								eventType: 'OrderActionRecord',
							})
						: undefined,
					beyondMaxLookback: !!result.body?.beyondMaxLookback,
				},
			};
		} catch (e: any) {
			console.error(`Caught error getting market trade history ${e}`);
			throw e;
		}
	}

	/**
	 *
	 * @param marketIndex
	 * @param from - unix timestamp ms since epoch
	 * @param to - unix timestamp ms since epoch
	 * @returns
	 */
	public static async getFundingRates(
		marketIndex: number,
		from: number,
		to: number
	): ClientResponse<{
		data: UISerializableFundingRateRecord[];
	}> {
		try {
			const result = await this.get(
				`/fundingRates?marketIndex=${marketIndex}&from=${from}&to=${to}`
			);

			if (!result.success || result.body.status !== 'ok') {
				return { success: false, message: `Couldn't get market trade history` };
			}

			return {
				success: true,
				body: {
					data: result.body.fundingRates.map((record: any) =>
						Serializer.Deserialize.UIFundingRate(record)
					),
				},
			};
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting funding history ${e}`);
			return {
				success: false,
				message: `Caught Error getting funding rates`,
			};
		}
	}

	/**
	 *
	 * @param marketIndex
	 * @returns
	 */
	public static async getPrice24hrsAgo(
		marketIndex: number,
		marketType: MarketType
	): ClientResponse<{
		data: {
			price: number;
		};
	}> {
		if (process.env.NEXT_PUBLIC_IGNORE_STATS) {
			return {
				success: true,
				body: {
					data: {
						price: 0,
					},
				},
			};
		}

		try {
			const result = await (this.get(
				`/stats/price24hrsAgo?marketIndex=${marketIndex}&marketType=${ENUM_UTILS.toStr(
					marketType
				)}`
			) as ClientResponse<{
				data: {
					price: number;
				};
			}>);

			if (!result.success) {
				return { success: false, message: `Error` };
			}

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught Error price 24hrs ago ${e}`);
			return {
				success: false,
				message: `Caught Error price 24hrs ago`,
			};
		}
	}

	public static async getRecentTrades(marketId: MarketId): ClientResponse<{
		data: {
			trades: UISerializableOrderActionRecord[];
			mostRecentTradeScore: number;
		};
	}> {
		try {
			const result = await this.get(
				`/trades?marketIndex=${
					marketId.marketIndex
				}&marketType=${ENUM_UTILS.toStr(marketId.marketType)}`
			);

			const formattedTrades = result.body.data.trades.map((record: string) =>
				Serializer.Deserialize.UIOrderActionRecord(JSON.parse(record))
			);

			const mostRecentTradeScore = result.body.data.mostRecentTradeScore;

			LoggingService.debug(
				`Initial most recent trade score : ${mostRecentTradeScore}`
			);

			if (result.success) {
				return {
					success: true,
					body: {
						data: {
							trades: formattedTrades,
							mostRecentTradeScore,
						},
					},
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			LoggingService.error(`Caught error getting recent trades`);
			return { success: false };
		}
	}

	public static async getNewTrades(
		marketIndex: number,
		marketType: MarketType,
		mostRecentTradeScore: number
	): ClientResponse<{
		data: {
			trades: UISerializableOrderActionRecord[];
			mostRecentTradeScore: number;
			requestWasStale: boolean;
		};
	}> {
		try {
			const result = await this.get(
				`/newTrades?marketIndex=${marketIndex}&marketType=${ENUM_UTILS.toStr(
					marketType
				)}&mostRecentTradeScore=${mostRecentTradeScore}`
			);

			const formattedTrades = result.body.data.trades.map((record: string) =>
				Serializer.Deserialize.UIOrderActionRecord(JSON.parse(record))
			);

			const newMostRecentTradeScore = result.body.data.mostRecentTradeScore;

			if (result.success) {
				return {
					success: true,
					body: {
						data: {
							trades: formattedTrades,
							mostRecentTradeScore: newMostRecentTradeScore,
							requestWasStale: result.body.data.requestWasStale,
						},
					},
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			LoggingService.error(`Caught error getting new trades`);
			return { success: false };
		}
	}

	public static async getFundingForUsers(
		userPubKeys: PublicKey[],
		pageIndex: number,
		oldPageIndex?: number,
		beforeTs?: number,
		afterTs?: number,
		pageSize = DEFAULT_PAGE_SIZE
	): ClientResponse<{
		data: {
			records: UISerializableFundingPaymentRecord[][];
			totalCounts: number[];
		};
	}> {
		try {
			const queryString = getPaginationQueryString({
				baseQuery: `/fundingRatePayments?userPublicKeys=${joinKeys(
					userPubKeys
				)}`,
				newPage: pageIndex,
				pageSize,
				oldPage: oldPageIndex,
				beforeTs,
				afterTs,
			});

			const result = await this.get(queryString);

			const resultData = result.body.data as {
				records: any[][];
				totalCounts: number[];
			};

			const parsedRecords = resultData?.records?.map((records) =>
				records.map(Serializer.Deserialize.UIFundingPayment)
			);

			if (result.success) {
				return {
					success: true,
					body: {
						data: {
							records: parsedRecords,
							totalCounts: resultData?.totalCounts,
						},
					},
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting recent trades`);
			return { success: false };
		}
	}

	public static async getLiquidationHistoryForUsers(
		userPubKeys: PublicKey[],
		pageIndex: number,
		pageSize = DEFAULT_PAGE_SIZE
	): ClientResponse<{
		data: {
			liquidations: UISerializableLiquidationRecord[][];
			totalCounts: number[];
		};
	}> {
		try {
			const result = await this.get(
				`/liquidations/userAccount/?userPublicKey=${joinKeys(
					userPubKeys
				)}&targetPageIndex=${pageIndex}&pageSize=${pageSize}`
			);

			const { liquidations: allUserLiquidations, totalCounts } = result.body
				.data as { liquidations: any[]; totalCounts: number[] };

			const parsedLiquidations = allUserLiquidations.map((userLiquidations) =>
				userLiquidations.map((liquidation: JsonObject) =>
					Serializer.Deserialize.UILiquidation(liquidation)
				)
			);

			if (result.success) {
				return {
					success: true,
					body: {
						data: {
							liquidations: parsedLiquidations,
							totalCounts,
						},
					},
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting liquidation history`);
			return { success: false };
		}
	}

	public static async getLiquidationCountForUser(
		userPubKey: PublicKey
	): ClientResponse<{ data: number }> {
		try {
			const result = await this.get(
				`/liquidations/userAccountLiqCount/?userPublicKey=${userPubKey.toString()}`
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting liquidation history`);
			return { success: false };
		}
	}

	public static async getLiquidationHistoryForLiquidator(
		userAccountPubKey: PublicKey,
		pageIndex: number,
		pageSize = DEFAULT_PAGE_SIZE
	): ClientResponse<{ data: UISerializableLiquidationRecord[] }> {
		try {
			const result = await this.get(
				`/liquidations/userAccount/?liquidator=${userAccountPubKey.toString()}&targetPageIndex=${pageIndex}&pageSize=${pageSize}`
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting liquidation history`);
			return { success: false };
		}
	}

	public static async getLiquidations(
		pageIndex: number,
		pageSize = DEFAULT_PAGE_SIZE
	): ClientResponse<{
		data: {
			liquidations: UISerializableLiquidationRecord[];
			totalCount: number;
		};
	}> {
		try {
			const result = await this.get(
				`/liquidations/?targetPageIndex=${pageIndex}&pageSize=${pageSize}`
			);

			const { liquidations: liquidations, totalCount } = result.body.data as {
				liquidations: any[];
				totalCount: number;
			};

			const parsedLiquidations = liquidations.map((liquidation) =>
				Serializer.Deserialize.UILiquidation(liquidation)
			);

			if (result.success) {
				return {
					success: true,
					body: {
						data: {
							liquidations: parsedLiquidations,
							totalCount,
						},
					},
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting liquidations history`);
			return { success: false };
		}
	}

	public static async getBankruptcies(
		pageIndex: number,
		oldPageIndex?: number,
		beforeTs?: number,
		afterTs?: number,
		pageSize = DEFAULT_PAGE_SIZE
	): ClientResponse<{
		data: {
			bankruptcies: UISerializableLiquidationRecord[];
			totalCount: number;
		};
	}> {
		try {
			const queryString = getPaginationQueryString({
				baseQuery: '/bankruptcies',
				newPage: pageIndex,
				pageSize,
				oldPage: oldPageIndex,
				beforeTs,
				afterTs,
			});

			const result = await this.get(queryString);

			const { bankruptcies: bankruptcies, totalCount } = result.body.data as {
				bankruptcies: any[];
				totalCount: number;
			};

			const parsedBankruptcies = bankruptcies.map((bankruptcy) =>
				Serializer.Deserialize.UILiquidation(bankruptcy)
			);

			if (result.success) {
				return {
					success: true,
					body: {
						data: {
							bankruptcies: parsedBankruptcies,
							totalCount,
						},
					},
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting bankruptcies history`);
			return { success: false };
		}
	}

	public static async getDepositHistoryForUsers(
		userPubKeys: PublicKey[],
		pageIndex: number,
		oldPageIndex?: number,
		beforeTs?: number,
		afterTs?: number,
		explanation?: DepositExplanation,
		pageSize = DEFAULT_PAGE_SIZE
	): ClientResponse<{
		data: {
			records: UISerializableDepositRecord[][];
			totalCounts: number[];
			maxRecordLimit: number;
		};
	}> {
		try {
			const queryString = getPaginationQueryString({
				baseQuery: `/deposits/userAccounts/?userPublicKeys=${joinKeys(
					userPubKeys
				)}${
					explanation ? `&explanation=${ENUM_UTILS.toStr(explanation)}` : ''
				}`,
				newPage: pageIndex,
				pageSize,
				oldPage: oldPageIndex,
				beforeTs,
				afterTs,
			});

			const result = await this.get(queryString);

			const deserializedDeposits = result.body.data.records.map(
				(records: JsonObject[]) =>
					records.map((record) => Serializer.Deserialize.UIDeposit(record))
			);

			if (result.success) {
				return {
					success: true,
					body: {
						data: {
							records: deserializedDeposits,
							totalCounts: result.body.data.totalCounts,
							maxRecordLimit: result.body.data.maxRecordLimit,
						},
					},
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting deposit history`);
			return { success: false };
		}
	}

	public static async getVolumeData(
		marketIndex?: number,
		marketType?: MarketType
	): ClientResponse<{
		data: {
			W: { date: string; volume: number }[];
			D: { date: string; volume: number }[];
			H: { date: string; volume: number }[];
		};
	}> {
		try {
			const result = await this.get(
				`/stats/volume?marketIndex=${marketIndex ?? ''}&marketType=${
					ENUM_UTILS.toStr(marketType as MarketType) ?? ''
				}`
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting volume data`);
			return { success: false };
		}
	}

	public static async get24HourMarketsData(): ClientResponse<{
		data: MarketDetails24H[];
	}> {
		try {
			const result = await this.get(`/markets24h`);
			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting 24 hour volume`);
			return { success: false };
		}
	}

	public static async get24HourPricePoints(): ClientResponse<{
		data: {
			marketIndex: number;
			pricePoints: number[];
			marketType: MarketType;
		}[];
	}> {
		try {
			const result = await this.get(`/stats/24hAgoPricePoints`);
			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting 24 hour volume`);
			return { success: false };
		}
	}

	public static async get24HourVolume(
		marketIndex?: number,
		marketType?: MarketType
	): ClientResponse<{
		data: { volume: number };
	}> {
		try {
			const result = await this.get(
				`/stats/24HourVolume?marketIndex=${marketIndex ?? ''}&marketType=${
					ENUM_UTILS.toStr(marketType as MarketType) ?? ''
				}`
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting 24 hour volume`);
			return { success: false };
		}
	}

	public static async get1WVolume(
		marketIndex?: number,
		marketType?: MarketType
	): ClientResponse<{
		data: { volume: number };
	}> {
		try {
			const result = await this.get(
				`/stats/1WVolume?marketIndex=${marketIndex ?? ''}&marketType=${
					ENUM_UTILS.toStr(marketType as MarketType) ?? ''
				}`
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting 1W volume`);
			return { success: false };
		}
	}

	public static async getRolling24HourVolume(
		marketIndex?: number,
		marketType?: MarketType
	): ClientResponse<{
		data: { volume: string; hour: number }[];
	}> {
		try {
			const result = await this.get(
				`/stats/24HourRollingVolume?marketIndex=${
					marketIndex ?? ''
				}&marketType=${ENUM_UTILS.toStr(marketType as MarketType) ?? ''}`
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting rolling 24 hour volume`);
			return { success: false };
		}
	}

	public static async getAllTimeCumulativeVolume(
		marketIndex?: number,
		marketType?: MarketType,
		resolution?: CandleResolution
	): ClientResponse<{
		data: {
			allTimeTotalCumulativeVolumeData: {
				cumulativeVolume: number;
				volumeHistory: VolumeHistory[];
			};
			volumeDataForMarkets: {
				marketSymbol: string;
				marketType: MarketType;
				marketIndex: number;
				cumulativeVolume: {
					cumulativeVolume: number;
					volumeHistory: VolumeHistory[];
				};
			}[];
		};
	}> {
		try {
			const result = await this.get(
				`/stats/allTimeCumulativeVolume?marketIndex=${
					marketIndex ?? ''
				}&marketType=${
					ENUM_UTILS.toStr(marketType as MarketType) ?? ''
				}&resolution=${resolution ?? 'W'}`
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting cumulative volume`);
			return { success: false };
		}
	}

	public static async getTotalTraders(): ClientResponse<{
		data: { cumulative: number };
	}> {
		try {
			const result = await this.get(`/traders`);
			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting total trader count`);
			return { success: false };
		}
	}

	public static async get24hrAvgFundingRate(
		marketIndex: string
	): ClientResponse<{ data: { long: string; short: string } }> {
		try {
			const result = await this.get(
				`/24HrAvgFundingRate?marketIndex=${marketIndex}`
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting 24hr funding rate`);
			return { success: false };
		}
	}

	public static async getAvgFundingRates(): ClientResponse<{
		data: {
			[marketIndex: number]: {
				'24h': number;
				'7d': number;
				'30d': number;
				'1y': number;
			};
		};
	}> {
		try {
			const result = await this.get(`/stats/avgFundingRates`);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting avg funding rates`);
			return { success: false };
		}
	}

	public static async getExternalFundingRates(): ClientResponse<{
		data: {
			[marketIndex: number]: { [externalSource: string]: number };
		};
	}> {
		try {
			const result = await this.get(`/stats/externalFundingRates`);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error getting external funding rates`);
			return { success: false };
		}
	}

	public static async getLatestDeployment(): ClientResponse<{
		data: { deploymentId: string };
	}> {
		try {
			const result = await this.get(`/latestDeployment`);
			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting latest deployment`);
			return { success: false };
		}
	}

	public static async getServerStatus(): ClientResponse<{
		data: {
			maintenanceMode: boolean;
			maintenanceModeEstimateTs?: number;
		};
	}> {
		try {
			const result = await this.get(`/status`);

			const serverStatus = result.body;

			if (result.success) {
				return {
					success: true,
					body: {
						data: serverStatus,
					},
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			LoggingService.error(`Caught error getting history server status`);
			return { success: false };
		}
	}

	public static async getTopMakersKeysAndAccounts({
		marketIndex,
		marketType,
		side,
		limit,
	}: {
		marketIndex: number;
		marketType: MarketType;
		side: 'bid' | 'ask';
		limit: number;
	}): Promise<
		{
			userAccountPubKey: PublicKey;
			userAccount: UserAccount;
		}[]
	> {
		try {
			const queryParams = encodeQueryParams({
				marketIndex,
				marketType: ENUM_UTILS.toStr(marketType),
				side,
				limit,
				includeAccounts: true,
			});

			const result = await this.get(
				`/topMakers?${queryParams}`,
				RESOURCE.DLOB_SERVER
			);

			if (result.success) {
				return result.body.map(
					(value: { userAccountPubKey: string; accountBase64: string }) => {
						return {
							userAccountPubKey: new PublicKey(value.userAccountPubKey),
							userAccount: decodeUser(
								Buffer.from(value.accountBase64, 'base64')
							),
						};
					}
				);
			}

			return [];
		} catch (e: any) {
			console.log('error:', e);
			LoggingService.error(
				`Caught error fetching best makers from dlob server`
			);
			return [];
		}
	}

	public static async getUnsettledPnlUsersForMarket(
		marketIndex: number
	): Promise<{
		marketIndex: number;
		gainers: { userPubKey: PublicKey; pnl: BigNum }[];
		losers: { userPubKey: PublicKey; pnl: BigNum }[];
	}> {
		try {
			const queryParams = encodeQueryParams({
				marketIndex,
			});

			const result = await this.get(
				`/unsettledPnlUsers?${queryParams}`,
				RESOURCE.DLOB_SERVER
			);

			if (result.success) {
				const resp = result.body;

				return {
					marketIndex: resp.marketIndex,
					gainers:
						resp.gainers
							?.filter(
								(gainer: { userPubKey: PublicKey; pnl: number }) =>
									gainer.pnl > 0
							)
							.map((gainer: { userPubKey: PublicKey; pnl: number }) => {
								return {
									userPubKey: new PublicKey(gainer.userPubKey),
									pnl: BigNum.fromPrint(
										gainer.pnl.toString(),
										QUOTE_PRECISION_EXP
									),
								};
							}) ?? [],
					losers:
						resp.losers
							?.filter(
								(gainer: { userPubKey: PublicKey; pnl: number }) =>
									gainer.pnl < 0
							)
							.map((loser: { userPubKey: PublicKey; pnl: number }) => {
								return {
									userPubKey: new PublicKey(loser.userPubKey),
									pnl: BigNum.fromPrint(
										loser.pnl.toString(),
										QUOTE_PRECISION_EXP
									),
								};
							}) ?? [],
				};
			}

			return { marketIndex, gainers: [], losers: [] };
		} catch (e: any) {
			captureException(e);
			LoggingService.error(
				`Caught error fetching unsettled pnl users from dlob server`
			);
			return { marketIndex, gainers: [], losers: [] };
		}
	}

	public static async requestFileDownload({
		fileType,
		from,
		to,
		userPubKeys,
		authority,
		isEmulationMode,
		marketSymbol,
	}: {
		fileType: DownloadRecordType;
		from: string;
		to: string;
		userPubKeys: PublicKey[];
		authority: string;
		isEmulationMode: boolean;
		marketSymbol?: string;
	}): ClientResponse<{
		data: {
			file: string;
			fileTitle: string;
		};
	}> {
		try {
			const url = `/requestFileDownload?fileType=${fileType}&from=${from}&to=${to}&userPublicKeys=${joinKeys(
				userPubKeys
			)}&authority=${
				authority ?? ''
			}&isEmulationMode=${isEmulationMode}&market=${marketSymbol ?? ''}`;

			const result = await this.get(url);

			if (!result.success) {
				return {
					success: false,
					message:
						result.status === 429
							? 'Too many requests. Please wait before trying again.'
							: 'No data available for the selected time/record type.',
				};
			}

			return result;
		} catch (e: any) {
			console.log(`Caught error requesting file download`);
			return {
				success: false,
				message: `Error requesting file download.`,
			};
		}
	}

	public static async getDownloadRequests(
		authority: PublicKey,
		isEmulationMode: boolean
	): ClientResponse<{
		data: {
			records: {
				pending: DownloadFile[];
				available: DownloadFile[];
			};
			counts: {
				pending: number;
				available: number;
			};
		};
	}> {
		try {
			const result = await this.get(
				`/authorityDownloads/?authority=${authority.toString()}&isEmulationMode=${isEmulationMode}`
			);

			if (!result.success) {
				console.error(`Caught error getting download records`);
				return {
					success: false,
				};
			}

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(`Caught error getting download records`);
			return { success: false };
		}
	}

	/**
	 * @param startTs Start timestamp in seconds.
	 * @param endTs End timestamp in seconds.
	 * @param userAccounts User accounts to fetch volume for.
	 * @returns Volume data for the given user accounts.
	 */
	public static async fetchUserVolume({
		startTs,
		endTs,
		userAccounts,
	}: {
		startTs: number;
		endTs: number;
		userAccounts: PublicKey[];
	}): ClientResponse<{
		data: { volumes: number[] };
	}> {
		try {
			const url = `/userVolume?startTs=${startTs}&endTs=${endTs}&userAccounts=${userAccounts
				.map((acc) => acc.toString())
				.join(',')}`;

			const result = await this.get(url);

			return result;
		} catch (err) {
			console.log(`Caught error fetching user volume`);
			return {
				success: false,
				message: `Error fetching user volume.`,
			};
		}
	}

	public static async getPythCranks(
		source: 'pythLazer' | 'pythPull',
		feedIds: number[] | string[]
	): ClientResponse<{
		data: string;
	}> {
		try {
			const result = await this.get(
				`/${source}?feedIds=${feedIds.join(',')}`,
				RESOURCE.DLOB_SERVER
			);

			return result;
		} catch (e: any) {
			captureException(e);
			console.error(e, `Caught error pyth crank data`);
			return { success: false };
		}
	}
}

export default ExchangeHistoryClient;
